import "./style.scss";

import Close from "@mui/icons-material/Close";
import CloseIcon from "@mui/icons-material/Close";
import { Modal } from "@mui/material";
import { Tooltip } from "@mui/material";
import PDF from "assets/img/svg/pdf";
import SendEmailIcon from "assets/img/svg/sendEmail";
import { getCleanLanguageCode } from "components/Beneficiar/utils";
import ModalBox from "components/Modal";
import InsertVariable from "components/shared/InsertVariableMenu";
import { PortalType } from "contexts/portal.context";
import type { IOutdatedDocument } from "helpers/beneficiaryDocuments";
import { formatShortDate } from "helpers/beneficiaryDocuments";
import { getStatementFilename } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import type { ChangeEvent, KeyboardEvent } from "react";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import type { ITermBeneficiar } from "store/managementStore";
import { setLastSentAtBeneficiar } from "store/managementStore";
import { EnumCompanySettings, fetchCompanyInfo } from "store/settingsStore";
import {
    sendBeneficiaryMergedStatements,
    sendBulkMailWithBeneficiarStatement,
    sendMailWithBeneficiarStatement,
} from "store/statementsStore";
import type { AppDispatch, RootState } from "store/store";
import { EStatementType } from "types";

import ButtonBase from "../ButtonBase";
import StyledButton from "../StyledButton";
import { StyledMenuItem, StyledSelect } from "../StyledSelect";
import { buildBulkBodyByLang, buildBulkSubjectByLang, getEmailContent, getEmailSubject } from "./helpers";
import MergedStatementsMailBody from "./MergedStatementsMailBody";

interface ISendEmailBeneficiary {
    /**
     *
     */
    isModalOpen: boolean;
    /**
     * Array with beneficiaries selected (there can be only one inside if only one selected)
     */
    beneficiaries: ITermBeneficiar[];
    /**
     * Array with beneficiaries having a custom statement associated
     */
    hasCustomStatementsBeneficiars: ITermBeneficiar[];
    /**
     * Id of the current period.
     */
    periodId: number | undefined;
    /**
     * Triggers closing of email modal.
     */
    onClose: VoidFunction;
    /**
     * When doing a selection, array with beneficiaries that have no email.
     */
    haveNoEmail?: ITermBeneficiar[];
    /**
     * Whether there are several beneficiaries selected or not.
     */
    isBulk: boolean;
    /**
     * Documents reminder mode: the composer uses the documents reminder template
     * (no statement attachment, no period) and delegates the sending.
     */
    documentsReminder?: {
        onSend: (payload: { subject: string; body: string; mailCc: string[] }) => void | Promise<void>;
        /** What `{{documents}}` will contain for one of the recipients, shown under the message */
        preview?: { beneficiaryName: string; documents: IOutdatedDocument[] };
    };
}

type Mode = "default" | "merged" | "merged-gross";

const SendEmailBeneficiar = ({
    isModalOpen,
    beneficiaries,
    periodId,
    onClose,
    haveNoEmail,
    isBulk,
    hasCustomStatementsBeneficiars,
    documentsReminder,
}: ISendEmailBeneficiary) => {
    const isDocumentsReminder = !!documentsReminder;
    const emailKind = isDocumentsReminder ? "documents" : "statement";
    const [t] = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const companySettings = useSelector((state: RootState) => state.settings.companySettings);
    const { companyLanguage, getCompanySettings, countryFeatures } = useCompanySettings();
    const { showNet, showPrecount } = countryFeatures.statementBlocks;

    const portalType = useContext(PortalType);
    const emailLanguage = (isBulk ? companyLanguage : getCleanLanguageCode(beneficiaries[0].lang)) ?? "fr";
    // Documents reminder: the operator picks the fr / en template, whatever the derived language
    const [templateLanguage, setTemplateLanguage] = useState<"fr" | "en">(emailLanguage === "en" ? "en" : "fr");
    const contentLanguage = isDocumentsReminder ? templateLanguage : emailLanguage;
    const recipientsCount = beneficiaries.filter((beneficiary) => beneficiary.email).length;

    const defaultEmail = useMemo(() => {
        const settingName = isDocumentsReminder
            ? EnumCompanySettings[`beneficiaryDocumentsReminderTemplate${contentLanguage.toUpperCase()}`]
            : EnumCompanySettings[`beneficiaryMailTemplate${emailLanguage.toUpperCase()}`];

        return companySettings?.settings.find((s) => s.name === settingName);
    }, [companySettings, beneficiaries, contentLanguage]);

    const isPrecounted =
        beneficiaries[0]?.precount == 1 ||
        (beneficiaries[0]?.precount == 2 && (beneficiaries[0]?.contributions ?? 0) > 0);

    const isMergedStatementsEnabled = useMemo(() => {
        return getCompanySettings("beneficiary_merged_statements_enabled");
    }, [getCompanySettings]);

    // const isBulk = Array.isArray(beneficiar)
    const hasAuthorPortalAccess =
        !isDocumentsReminder &&
        !isBulk &&
        beneficiaries[0]?.accountStatus &&
        beneficiaries[0]?.accountStatus == "confirmed";

    const [emails, setEmails] = useState<string[]>(defaultEmail?.content?.mailCC ?? []);
    const [hasCC, setHasCC] = useState(!!(defaultEmail?.content?.mailCC ?? ""));
    const [mailInput, setMailInput] = useState("");
    const [ignore, setIgnore] = useState<EStatementType[]>([]);
    const [selectedMode, setMode] = useState<Mode>("default");

    const mode = useMemo<Mode>(() => {
        return isBulk || !isMergedStatementsEnabled || isDocumentsReminder ? "default" : selectedMode;
    }, [selectedMode, isBulk, isMergedStatementsEnabled]);

    const emailSubject = useMemo(() => {
        return getEmailSubject({
            defaultSubject: defaultEmail?.content?.subject,
            isBulk,
            beneficiary: beneficiaries[0],
            lang: contentLanguage,
            kind: emailKind,
        });
    }, [beneficiaries, defaultEmail, contentLanguage]);

    const emailContent = useMemo(() => {
        return getEmailContent({
            defaultBody: defaultEmail?.content.body,
            isBulk,
            beneficiary: beneficiaries[0],
            companyName: companySettings?.name ?? "",
            lang: contentLanguage,
            kind: emailKind,
        });
    }, [beneficiaries, companySettings, defaultEmail, contentLanguage]);

    /**
     * In bulk mode the backend picks per beneficiary language from these maps
     * so every recipient receives the email in their own language.
     */
    const subjectByLang = useMemo(
        () => buildBulkSubjectByLang({ defaultSubject: defaultEmail?.content?.subject }),
        [defaultEmail],
    );

    const bodyByLang = useMemo(
        () =>
            buildBulkBodyByLang({
                defaultBody: defaultEmail?.content?.body,
                companyName: companySettings?.name ?? "",
            }),
        [defaultEmail, companySettings],
    );

    const [subjectInput, setSubjectInput] = useState(emailSubject ?? "");

    const [bodyInput, setBodyInput] = useState(emailContent ?? "");
    const [cursorPosition, setCursorPosition] = useState(0);

    const previewDetail = (document: IOutdatedDocument) => {
        if (document.status === "missing") return t("SendEmail.DocumentsPreviewMissing");
        const date = formatShortDate(document.validUntil as string);

        return t(
            document.status === "expired" ? "SendEmail.DocumentsPreviewExpired" : "SendEmail.DocumentsPreviewExpires",
            { date },
        );
    };

    useEffect(() => {
        emailSubject && setSubjectInput(emailSubject);
    }, [emailSubject]);

    useEffect(() => {
        emailContent && setBodyInput(emailContent);
    }, [emailContent]);

    // Documents reminder: switching the fr / en template also reloads its CC list
    useEffect(() => {
        if (!isDocumentsReminder) return;
        const mailCC: string[] = defaultEmail?.content?.mailCC ?? [];
        setEmails(mailCC);
        setHasCC(mailCC.length > 0);
    }, [defaultEmail, isDocumentsReminder]);

    /**
     * This returns an array with the name of beneficiaries that won't receive the mail :
     * if the beneficiar has no email address
     * if it's a custom statement
     */
    const beneficiariesNotIncluded: ITermBeneficiar[] = useMemo(() => {
        if (haveNoEmail?.length && hasCustomStatementsBeneficiars.length)
            return haveNoEmail.concat(hasCustomStatementsBeneficiars);
        if (haveNoEmail?.length) return haveNoEmail;
        if (hasCustomStatementsBeneficiars?.length) return hasCustomStatementsBeneficiars;
        else return [];
    }, [haveNoEmail, hasCustomStatementsBeneficiars]);

    const sendEmail = () => {
        if (isDocumentsReminder) {
            documentsReminder?.onSend({ subject: subjectInput, body: bodyInput, mailCc: emails });
            onClose();
            return;
        }
        if (periodId) {
            if (mode === "merged" && !isBulk) {
                dispatch(
                    sendBeneficiaryMergedStatements({
                        beneficiaryId: beneficiaries[0]?.id,
                        periodId,
                        sendToBeneficiary: true,
                        mailCc: emails,
                        subject: subjectInput,
                        body: bodyInput,
                    }),
                );
            } else if (mode === "merged-gross" && !isBulk) {
                dispatch(
                    sendBeneficiaryMergedStatements({
                        beneficiaryId: beneficiaries[0]?.id,
                        periodId,
                        sendToBeneficiary: true,
                        mailCc: emails,
                        grossStatementsOnly: true,
                        subject: subjectInput,
                        body: bodyInput,
                    }),
                );
            } else {
                if (isBulk) {
                    /**
                     * If user edited subject/body, send their text as-is to all recipients.
                     * Otherwise send per-language defaults so each beneficiary receives in their own language.
                     * Normalize with `?? ""` because `emailSubject`/`emailContent` can be `undefined`
                     * when no company email template is configured — `subjectInput`/`bodyInput` are then
                     * `""` and would spuriously look edited.
                     */
                    const userEdited = subjectInput !== (emailSubject ?? "") || bodyInput !== (emailContent ?? "");
                    dispatch(
                        sendBulkMailWithBeneficiarStatement({
                            beneficiarId: beneficiaries.map((item: any) => item.id),
                            periodId,
                            mailCc: emails,
                            subject: subjectInput,
                            body: bodyInput,
                            subjectByLang: userEdited ? undefined : subjectByLang,
                            bodyByLang: userEdited ? undefined : bodyByLang,
                            ignore,
                        }),
                    );
                    setIgnore([]);
                    dispatch(setLastSentAtBeneficiar(beneficiaries.map((item: any) => item.id)));
                } else {
                    dispatch(
                        sendMailWithBeneficiarStatement({
                            beneficiarId: beneficiaries[0]?.id,
                            periodId,
                            mailCc: emails,
                            subject: subjectInput,
                            body: bodyInput,
                            ignore,
                        }),
                    );
                    setIgnore([]);
                    dispatch(setLastSentAtBeneficiar([beneficiaries[0]?.id]));
                }
            }
        }
        onClose();
    };

    const addEmail = (email: string) => {
        const emailRegex = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;
        if (!emailRegex.test(email)) return;
        setEmails((emails) => Array.from(new Set([...emails, email])));
        setMailInput("");
    };

    const handleAddMail = (e: ChangeEvent<HTMLInputElement>) => {
        setMailInput(e.target.value);
        if (e.target.value.indexOf(",") < 0) return;
        addEmail(e.target.value.replace(/ /g, "").slice(0, -1));
    };

    const handleAddMailWithEnter = (e: KeyboardEvent<HTMLInputElement>) =>
        (e.key == "Enter" || e.code == "Space") && addEmail((e.target as HTMLInputElement).value.replace(/ /g, ""));

    const handleAddMailWithBlur = (e: any) => addEmail(e.target.value);

    const handleRemoveMail = (index: number) => {
        setEmails((emails) => [...emails.filter((item, i) => i != index)]);
    };

    const handleCCButton = () => {
        if (hasCC) {
            setEmails([]);
            setHasCC(false);
        } else {
            setEmails(defaultEmail?.content?.mailCC ?? []);
            setHasCC(true);
        }
    };

    const formatTooltip = (beneficiariesNotIncluded: ITermBeneficiar[]) => {
        return beneficiariesNotIncluded
            ?.map((beneficiar: ITermBeneficiar) => `${beneficiar.name ?? ""} ${beneficiar.surname ?? ""}`)
            .join(", ");
    };

    useEffect(() => {
        if (!companySettings) dispatch(fetchCompanyInfo());
    }, [companySettings, dispatch]);

    return (
        <>
            {hasAuthorPortalAccess ? (
                <ModalBox open={isModalOpen} onClose={onClose} mainTitle={t("Summary.SendToBeneficiarPortal")}>
                    <div className="send-email-portal-modal">
                        <div className="send-email-portal-modal-body">{t("Summary.PortalSendMessage")}</div>
                        <div className="send-email-portal-modal-footer">
                            <StyledButton buttonType="primary" style={{ width: "100%" }} onClick={sendEmail}>
                                {t("SendEmail.Transmit")}
                            </StyledButton>
                        </div>
                    </div>
                </ModalBox>
            ) : (
                <Modal
                    open={isModalOpen}
                    onClose={() => {
                        onClose();
                        setIgnore([]);
                        setEmails(defaultEmail?.content?.mailCC ?? []);
                    }}
                >
                    <div className="send-email-beneficiary">
                        {beneficiariesNotIncluded.length > 0 && (
                            <div className="selection-error-beneficiar">
                                <div className="selection-error-txt">
                                    <span>{t("SendEmail.NoEmail")}</span>
                                    {!!haveNoEmail?.length && (
                                        <div className="li-error-wrapper">
                                            <span>- {t("SendEmail.EmailIsMissing")}</span>
                                            <Tooltip arrow placement="top" title={formatTooltip(haveNoEmail)}>
                                                <span className="see-names">{t("SendEmail.SeeNames")}</span>
                                            </Tooltip>
                                        </div>
                                    )}
                                    {!!hasCustomStatementsBeneficiars?.length && (
                                        <div className="li-error-wrapper">
                                            <span>- {t("SendEmail.IsCustomStatement")}</span>
                                            <Tooltip
                                                arrow
                                                placement="top"
                                                title={formatTooltip(hasCustomStatementsBeneficiars)}
                                            >
                                                <span className="see-names">{t("SendEmail.SeeNames")}</span>
                                            </Tooltip>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        <div className="send-email-modal">
                            <div className="send-email-header">
                                <div>
                                    <div className="send-email-header-title">
                                        {t(
                                            isDocumentsReminder
                                                ? "SendEmail.DocumentsReminderTitle"
                                                : "SendEmail.SendEmail",
                                        )}
                                    </div>
                                    <div className="send-email-header-subtitle">
                                        {isDocumentsReminder
                                            ? t("SendEmail.DocumentsReminderSubtitle", { count: recipientsCount })
                                            : t("SendEmail.HeaderSubtitle")}
                                    </div>
                                </div>
                                <div
                                    className="send-email-header-cross"
                                    onClick={() => {
                                        onClose();
                                        setEmails(defaultEmail?.content?.mailCC ?? []);
                                    }}
                                >
                                    <CloseIcon />
                                </div>
                            </div>
                            {isDocumentsReminder && (
                                <div className="my-4!">
                                    <StyledSelect
                                        value={templateLanguage}
                                        onChange={(e) => setTemplateLanguage(e.target.value as "fr" | "en")}
                                        sx={{ width: "100%" }}
                                        size="small"
                                    >
                                        <StyledMenuItem value="fr">{t("SendEmail.DocumentsTemplateFR")}</StyledMenuItem>
                                        <StyledMenuItem value="en">{t("SendEmail.DocumentsTemplateEN")}</StyledMenuItem>
                                    </StyledSelect>
                                </div>
                            )}
                            {!isBulk && isMergedStatementsEnabled && (
                                <div className="my-4!">
                                    <StyledSelect
                                        value={mode}
                                        onChange={(e) => {
                                            setMode(e.target.value as Mode);
                                        }}
                                        sx={{ width: "100%" }}
                                        size="small"
                                    >
                                        <StyledMenuItem value="default">
                                            {t("SendEmail.AllConsolidatedMode")}
                                        </StyledMenuItem>
                                        <StyledMenuItem value="merged">
                                            {t("SendEmail.MergedStatementsMode")}
                                        </StyledMenuItem>
                                        <StyledMenuItem value="merged-gross">
                                            {t("SendEmail.MergedGrossStatementsMode")}
                                        </StyledMenuItem>
                                    </StyledSelect>
                                </div>
                            )}
                            <div className="send-email-body">
                                <div className="send-email-body-from">
                                    <div className="send-email-body-from-left">
                                        <div className="send-email-body-from-from">{t("SendEmail.To")} </div>
                                        <div className="send-email-body-chip">
                                            {!isBulk ? (
                                                <>
                                                    <div className="send-email-body-avatar">
                                                        {(beneficiaries[0]?.surname ?? "")
                                                            .slice(0, 1)
                                                            .toLocaleUpperCase()}
                                                        {(beneficiaries[0]?.name ?? "").slice(0, 1).toLocaleUpperCase()}
                                                    </div>
                                                    <div className="send-email-body-text">
                                                        {beneficiaries[0]?.surname ?? ""} {beneficiaries[0]?.name ?? ""}
                                                        {beneficiaries[0]?.email && ` (${beneficiaries[0]?.email})`}
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="send-email-body-text">
                                                    {t("SendEmail.SelectedBeneficiaries")}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="send-email-body-from-right">
                                        <div
                                            className={`send-email-body-from-cc${(hasCC && " active") || ""}`}
                                            onClick={handleCCButton}
                                        >
                                            Cc
                                        </div>
                                    </div>
                                </div>
                                {hasCC && (
                                    <div className="send-email-body-cc">
                                        <div className="send-email-body-cc-text">Cc: </div>
                                        <div className="send-email-body-cc-mails">
                                            {emails.length > 0 &&
                                                emails.map((item, i) => (
                                                    <div className="send-email-body-chip" key={i}>
                                                        <div className="send-email-body-avatar">
                                                            {item.slice(0, 1).toLocaleUpperCase()}
                                                        </div>
                                                        <Tooltip title={item} arrow>
                                                            <div className="send-email-body-text">
                                                                {item.split("@")[0]}
                                                            </div>
                                                        </Tooltip>
                                                        <div
                                                            className="send-email-body-close"
                                                            onClick={() => handleRemoveMail(i)}
                                                        >
                                                            <Close />
                                                        </div>
                                                    </div>
                                                ))}
                                            <input
                                                type="text"
                                                className="send-email-body-cc-mail-input"
                                                value={mailInput}
                                                onChange={handleAddMail}
                                                onKeyDown={handleAddMailWithEnter}
                                                onBlur={handleAddMailWithBlur}
                                            />
                                        </div>
                                    </div>
                                )}
                                <input
                                    type="text"
                                    className="send-email-body-subject"
                                    value={subjectInput}
                                    onChange={(e) => setSubjectInput(e.target.value)}
                                />
                                <div className="send-email-body-content">
                                    <textarea
                                        className="send-email-body-content-textarea"
                                        name="email-content"
                                        id="email-content"
                                        draggable={false}
                                        value={bodyInput}
                                        onChange={(e) => setBodyInput(e.target.value)}
                                        onSelect={(e) => setCursorPosition(e.currentTarget.selectionStart)}
                                        autoFocus
                                    ></textarea>

                                    {(mode === "merged" || mode === "merged-gross") && (
                                        <MergedStatementsMailBody beneficiary={beneficiaries[0]} hideBody={true} />
                                    )}
                                </div>
                                {isDocumentsReminder && (
                                    <div className="send-email-documents">
                                        <div className="send-email-documents-variables">
                                            <InsertVariable
                                                cursorPosition={cursorPosition}
                                                field={bodyInput}
                                                variables={{
                                                    "{{name}}": t("EmailSettings.NameVariable"),
                                                    "{{documents}}": t("EmailSettings.DocumentsVariable"),
                                                }}
                                                onSelect={setBodyInput}
                                            />
                                            <span className="send-email-documents-help">
                                                {t("SendEmail.DocumentsVariableHelp")}
                                            </span>
                                        </div>
                                        {!!documentsReminder?.preview?.documents.length && (
                                            <div className="send-email-documents-preview">
                                                <span className="send-email-documents-preview-title">
                                                    {t("SendEmail.DocumentsPreviewFor", {
                                                        name: documentsReminder.preview.beneficiaryName,
                                                    })}
                                                </span>
                                                <ul>
                                                    {documentsReminder.preview.documents.map((document) => (
                                                        <li key={document.typeId}>
                                                            {document.typeName}{" "}
                                                            <span className={`send-email-documents-${document.status}`}>
                                                                ({previewDetail(document)})
                                                            </span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {!isDocumentsReminder && (
                                    <div className="send-email-body-attachments">
                                        {mode !== "merged" &&
                                            mode !== "merged-gross" &&
                                            !ignore.includes(EStatementType.CONSOLIDATED) && (
                                                <div className="send-email-body-attachment">
                                                    <CloseIcon
                                                        style={{
                                                            color: "grey",
                                                            width: "13px",
                                                            height: "13px",
                                                            cursor: "pointer",
                                                        }}
                                                        onClick={() =>
                                                            setIgnore((prev) => [...prev, EStatementType.CONSOLIDATED])
                                                        }
                                                    />
                                                    <PDF />
                                                    {isBulk ? (
                                                        <div
                                                            title={getStatementFilename(
                                                                "{{name}}",
                                                                "",
                                                                t("SendEmail.Consolidated", { lng: emailLanguage }),
                                                                new Date(),
                                                            )}
                                                            className="send-email-body-attachment-filename"
                                                        >
                                                            {getStatementFilename(
                                                                "{{name}}",
                                                                "",
                                                                t("SendEmail.Consolidated", { lng: emailLanguage }),
                                                                new Date(),
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div
                                                            title={getStatementFilename(
                                                                beneficiaries[0]?.beneficiaryName,
                                                                "",
                                                                t("SendEmail.Consolidated", { lng: emailLanguage }),
                                                                new Date(),
                                                            )}
                                                            className="send-email-body-attachment-filename"
                                                        >
                                                            {getStatementFilename(
                                                                beneficiaries[0]?.beneficiaryName,
                                                                "",
                                                                t("SendEmail.Consolidated", { lng: emailLanguage }),
                                                                new Date(),
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        {showNet &&
                                            mode !== "merged" &&
                                            mode !== "merged-gross" &&
                                            !ignore.includes(EStatementType.NET) && (
                                                <div className="send-email-body-attachment">
                                                    <CloseIcon
                                                        style={{
                                                            color: "grey",
                                                            width: "13px",
                                                            height: "13px",
                                                            cursor: "pointer",
                                                        }}
                                                        onClick={() =>
                                                            setIgnore((prev) => [...prev, EStatementType.NET])
                                                        }
                                                    />
                                                    <PDF />
                                                    {isBulk ? (
                                                        <div
                                                            title={getStatementFilename(
                                                                "{{name}}",
                                                                "",
                                                                t("SendEmail.Net", { lng: emailLanguage }),
                                                                new Date(),
                                                            )}
                                                            className="send-email-body-attachment-filename"
                                                        >
                                                            {getStatementFilename(
                                                                "{{name}}",
                                                                "",
                                                                t("SendEmail.Net", { lng: emailLanguage }),
                                                                new Date(),
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div
                                                            title={getStatementFilename(
                                                                beneficiaries[0]?.beneficiaryName,
                                                                "",
                                                                t("SendEmail.Net", { lng: emailLanguage }),
                                                                new Date(),
                                                            )}
                                                            className="send-email-body-attachment-filename"
                                                        >
                                                            {getStatementFilename(
                                                                beneficiaries[0]?.beneficiaryName,
                                                                "",
                                                                t("SendEmail.Net", { lng: emailLanguage }),
                                                                new Date(),
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        {showPrecount &&
                                            mode !== "merged" &&
                                            mode !== "merged-gross" &&
                                            !ignore.includes(EStatementType.PRECOUNT) &&
                                            isPrecounted && (
                                                <div className="send-email-body-attachment">
                                                    <CloseIcon
                                                        style={{
                                                            color: "grey",
                                                            width: "13px",
                                                            height: "13px",
                                                            cursor: "pointer",
                                                        }}
                                                        onClick={() =>
                                                            setIgnore((prev) => [...prev, EStatementType.PRECOUNT])
                                                        }
                                                    />
                                                    <PDF />
                                                    {isBulk ? (
                                                        <div
                                                            title={getStatementFilename(
                                                                "{{name}}",
                                                                "",
                                                                t("SendEmail.Precount", { lng: emailLanguage }),
                                                                new Date(),
                                                            )}
                                                            className="send-email-body-attachment-filename"
                                                        >
                                                            {getStatementFilename(
                                                                "{{name}}",
                                                                "",
                                                                t("SendEmail.Precount", { lng: emailLanguage }),
                                                                new Date(),
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div
                                                            title={getStatementFilename(
                                                                beneficiaries[0]?.beneficiaryName,
                                                                "",
                                                                t("SendEmail.Precount", { lng: emailLanguage }),
                                                                new Date(),
                                                            )}
                                                            className="send-email-body-attachment-filename"
                                                        >
                                                            {getStatementFilename(
                                                                beneficiaries[0]?.beneficiaryName,
                                                                "",
                                                                t("SendEmail.Precount", { lng: emailLanguage }),
                                                                new Date(),
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="send-email-footer">
                            <div className="send-email-isBulk">
                                {isDocumentsReminder
                                    ? t("SendEmail.DocumentsReminderFooter", { count: recipientsCount })
                                    : isBulk
                                      ? t("SendEmail.IsBulkBeneficiar")
                                      : ""}
                            </div>
                            <div className="send-email-button">
                                <ButtonBase
                                    variant="contained"
                                    onClick={sendEmail}
                                    disabled={
                                        (isDocumentsReminder && recipientsCount === 0) ||
                                        (beneficiaries.length === 1 &&
                                            haveNoEmail &&
                                            beneficiaries[0].id === haveNoEmail[0]?.id)
                                    }
                                >
                                    {t("SendEmail.Send")}
                                    <SendEmailIcon />
                                </ButtonBase>
                            </div>
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
};

export default SendEmailBeneficiar;
